import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Alert, Chip, Button,
  Skeleton, useTheme, alpha, Tooltip, IconButton, TextField,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, LinearProgress,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  HourglassEmpty as HourglassEmptyIcon,
  Groups as GroupsIcon,
  KingBed as KingBedIcon,
  Percent as PercentIcon,
  PieChartOutline as PieChartOutlineIcon,
  HeartBroken as HeartBrokenIcon,
  Login as LoginIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useUnidade } from '../../contexts/UnidadeContext';
import { saudeService } from '../../services/api';
import PainelNaoConfigurado from '../../components/saude/PainelNaoConfigurado';

// ─── Tipos ───

interface EstatisticasTotais {
  pacientes_dia: number;
  saidas: number;
  obitos: number;
  internacoes: number;
  tempo_medio_permanencia: number;
  leitos_ativos: number;
  leitos_operacionais: number;
  leitos_dia: number;
  leitos_dia_operacional: number;
  dias_periodo: number;
  taxa_ocupacao_hospitalar: number;
  taxa_ocupacao_operacional: number;
  taxa_mortalidade: number;
}

interface PorProfissional {
  profissional: string;
  codprofissional: number | null;
  internacoes: number;
  saidas: number;
  obitos: number;
  pacientes_dia: number;
  tempo_medio_permanencia: number;
  taxa_mortalidade: number;
}

interface PorUnidade {
  codunidade: string;
  nome: string;
  internacoes: number;
  saidas: number;
  obitos: number;
  pacientes_dia: number;
  tempo_medio_permanencia: number;
  leitos_ativos: number;
  leitos_dia: number;
  taxa_ocupacao_hospitalar: number;
  taxa_ocupacao_operacional: number;
  taxa_mortalidade: number;
}

interface EstatisticasInternacao {
  totais: EstatisticasTotais;
  por_profissional: PorProfissional[];
  por_unidade: PorUnidade[];
  periodo: { inicio: string; fim: string };
  gerado_em: string;
}

const REFRESH_MS = 60_000;

/** Data de hoje / 1o dia do mes corrente no formato YYYY-MM-DD. */
const hojeISO = (): string => new Date().toISOString().slice(0, 10);
const inicioMesISO = (): string => hojeISO().slice(0, 8) + '01';

/** Numero pt-BR (1 casa quando ha fracao). */
const fmt = (n: number | undefined | null): string =>
  (n ?? 0).toLocaleString('pt-BR', { maximumFractionDigits: 1 });

// ─── KPI Card ───

const KpiCard: React.FC<{
  label: string; value: string; sub?: string; icon: React.ReactNode; color: string; loading: boolean;
}> = ({ label, value, sub, icon, color, loading }) => {
  const theme = useTheme();
  return (
    <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{
          width: 48, height: 48, borderRadius: 2, display: 'flex', alignItems: 'center',
          justifyContent: 'center', bgcolor: alpha(color, 0.12), color, flexShrink: 0,
        }}>
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          {loading
            ? <Skeleton width={72} height={36} />
            : <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.1 }}>{value}</Typography>}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>{label}</Typography>
          {sub && !loading && (
            <Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>{sub}</Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

// ─── Pagina ───

const SaudeEstatisticasInternacao: React.FC = () => {
  const theme = useTheme();
  const { municipioAtivo } = useAuth();
  const { unidadeSelecionada } = useUnidade();

  const [data, setData] = useState<EstatisticasInternacao | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [naoConfigurado, setNaoConfigurado] = useState(false);
  const [ultimaAtualizacao, setUltimaAtualizacao] = useState<Date | null>(null);
  const [segundos, setSegundos] = useState(REFRESH_MS / 1000);

  // Filtro Periodo — default mes corrente (dia 1 -> hoje)
  const [inicio, setInicio] = useState<string>(inicioMesISO());
  const [fim, setFim] = useState<string>(hojeISO());

  const fetchRef = useRef<() => void>(() => {});

  const fetchData = useCallback(async (silencioso = false) => {
    if (!municipioAtivo?.id) return;
    if (!silencioso) setLoading(true);
    setError(null);
    try {
      const result = await saudeService.getEstatisticasInternacao(municipioAtivo.id, {
        unidade: unidadeSelecionada?.codunidade,
        inicio,
        fim,
      });
      setNaoConfigurado(result?.configurado === false);
      setData(result);
      setUltimaAtualizacao(new Date());
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Erro ao carregar as estatísticas de internação.');
    } finally {
      setLoading(false);
      setSegundos(REFRESH_MS / 1000);
    }
  }, [municipioAtivo?.id, unidadeSelecionada?.codunidade, inicio, fim]);

  useEffect(() => { fetchRef.current = () => fetchData(true); });

  // Busca inicial / ao trocar qualquer filtro
  useEffect(() => { fetchData(false); }, [fetchData]);

  // Auto-refresh a cada 60s (silencioso) + contador regressivo
  useEffect(() => {
    if (!municipioAtivo?.id) return;
    const refresh = setInterval(() => fetchRef.current(), REFRESH_MS);
    const tick = setInterval(() => setSegundos((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => { clearInterval(refresh); clearInterval(tick); };
  }, [municipioAtivo?.id]);

  const t = data?.totais;
  const porProfissional = data?.por_profissional || [];
  const porUnidade = data?.por_unidade || [];
  const mostrarUnidade = !unidadeSelecionada;

  if (!municipioAtivo?.id) {
    return <Box sx={{ p: 3 }}><Alert severity="info">Selecione um município para visualizar as estatísticas de internação.</Alert></Box>;
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Cabecalho */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HeartBrokenIcon color="primary" /> Estatísticas de Internação
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {unidadeSelecionada
              ? <>Unidade: <strong>{unidadeSelecionada.nome}</strong></>
              : 'Todas as unidades do município'}
            {' '}• Indicadores hospitalares do período
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {ultimaAtualizacao ? `Atualizado às ${ultimaAtualizacao.toLocaleTimeString('pt-BR')}` : 'Carregando...'}
            </Typography>
            <Typography variant="caption" color="text.disabled">Próxima atualização em {segundos}s</Typography>
          </Box>
          <Tooltip title="Atualizar agora">
            <span>
              <IconButton onClick={() => fetchData(false)} disabled={loading} color="primary"
                sx={{ border: `1px solid ${theme.palette.divider}` }}>
                <RefreshIcon sx={{ animation: loading ? 'spin 1s linear infinite' : 'none',
                  '@keyframes spin': { '100%': { transform: 'rotate(360deg)' } } }} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* Filtro Periodo */}
      <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, mb: 3 }}>
        <CardContent sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
          <TextField
            size="small" type="date" label="Período (início)"
            value={inicio} onChange={(e) => setInicio(e.target.value)}
            InputLabelProps={{ shrink: true }} sx={{ width: 180 }}
          />
          <TextField
            size="small" type="date" label="Período (fim)"
            value={fim} onChange={(e) => setFim(e.target.value)}
            InputLabelProps={{ shrink: true }} sx={{ width: 180 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 420 }}>
            Indicadores calculados para o período selecionado ({fmt(t?.dias_periodo)} dia{(t?.dias_periodo ?? 0) === 1 ? '' : 's'}).
            {mostrarUnidade ? ' Selecione uma Unidade de Saúde (acima) para focar em uma unidade.' : ''}
          </Typography>
        </CardContent>
      </Card>

      {naoConfigurado && <PainelNaoConfigurado />}
      {error && <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={() => fetchData(false)}>Tentar novamente</Button>}>{error}</Alert>}

      {/* 6 KPIs principais */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={4}>
          <KpiCard label="Tempo médio de permanência" value={`${fmt(t?.tempo_medio_permanencia)} dias`}
            sub={`${fmt(t?.saidas)} saída${(t?.saidas ?? 0) === 1 ? '' : 's'} no período`}
            icon={<HourglassEmptyIcon />} color="#ef6c00" loading={loading} />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <KpiCard label="Pacientes-dia" value={fmt(t?.pacientes_dia)}
            sub="Soma dos dias de internação no período"
            icon={<GroupsIcon />} color="#1565c0" loading={loading} />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <KpiCard label="Leitos-dia" value={fmt(t?.leitos_dia)}
            sub={`${fmt(t?.leitos_ativos)} leito${(t?.leitos_ativos ?? 0) === 1 ? '' : 's'} × ${fmt(t?.dias_periodo)} dia${(t?.dias_periodo ?? 0) === 1 ? '' : 's'}`}
            icon={<KingBedIcon />} color="#00838f" loading={loading} />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <KpiCard label="Taxa de ocupação hospitalar" value={`${fmt(t?.taxa_ocupacao_hospitalar)}%`}
            sub="Pacientes-dia / Leitos-dia (instalados)"
            icon={<PercentIcon />} color="#7b1fa2" loading={loading} />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <KpiCard label="Taxa de ocupação operacional" value={`${fmt(t?.taxa_ocupacao_operacional)}%`}
            sub="Pacientes-dia / Leitos-dia (não bloqueados)"
            icon={<PieChartOutlineIcon />} color="#283593" loading={loading} />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <KpiCard label="Taxa de mortalidade" value={`${fmt(t?.taxa_mortalidade)}%`}
            sub={`${fmt(t?.obitos)} óbito${(t?.obitos ?? 0) === 1 ? '' : 's'} / ${fmt(t?.saidas)} saída${(t?.saidas ?? 0) === 1 ? '' : 's'}`}
            icon={<HeartBrokenIcon />} color="#c62828" loading={loading} />
        </Grid>
      </Grid>

      {/* Chips de apoio */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
        <Chip size="small" variant="outlined" color="primary" icon={<LoginIcon sx={{ fontSize: 16 }} />} label={`Internações: ${fmt(t?.internacoes)}`} />
        <Chip size="small" variant="outlined" icon={<LogoutIcon sx={{ fontSize: 16 }} />} label={`Saídas: ${fmt(t?.saidas)}`} />
        <Chip size="small" variant="outlined" color="error" icon={<HeartBrokenIcon sx={{ fontSize: 16 }} />} label={`Óbitos: ${fmt(t?.obitos)}`} />
        <Chip size="small" variant="outlined" icon={<KingBedIcon sx={{ fontSize: 16 }} />} label={`Leitos ativos: ${fmt(t?.leitos_ativos)}`} />
        <Chip size="small" variant="outlined" label={`Leitos operacionais: ${fmt(t?.leitos_operacionais)}`} />
      </Box>

      {/* Por profissional */}
      <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonIcon fontSize="small" color="action" /> Por profissional responsável
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Indicadores das internações atribuídas a cada profissional no período.
          </Typography>
          {loading ? (
            <Skeleton variant="rectangular" height={160} />
          ) : porProfissional.length ? (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Profissional</TableCell>
                    <TableCell align="center">Internações</TableCell>
                    <TableCell align="center">Saídas</TableCell>
                    <TableCell align="center">Óbitos</TableCell>
                    <TableCell align="center">Pacientes-dia</TableCell>
                    <TableCell align="center">Permanência média</TableCell>
                    <TableCell align="right">Taxa de mortalidade</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {porProfissional.map((p, i) => (
                    <TableRow key={p.codprofissional ?? `sem-${i}`} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{p.profissional}</TableCell>
                      <TableCell align="center">{fmt(p.internacoes)}</TableCell>
                      <TableCell align="center">{fmt(p.saidas)}</TableCell>
                      <TableCell align="center">{fmt(p.obitos)}</TableCell>
                      <TableCell align="center">{fmt(p.pacientes_dia)}</TableCell>
                      <TableCell align="center">{fmt(p.tempo_medio_permanencia)} dias</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{fmt(p.taxa_mortalidade)}%</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography color="text.secondary" variant="body2">Nenhuma internação no período para os filtros selecionados.</Typography>
          )}
        </CardContent>
      </Card>

      {/* Por unidade (apenas quando nenhuma unidade selecionada) */}
      {mostrarUnidade && (
        <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Por unidade</Typography>
            {loading ? (
              <Skeleton variant="rectangular" height={160} />
            ) : porUnidade.length ? (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Unidade</TableCell>
                      <TableCell align="center">Internações</TableCell>
                      <TableCell align="center">Saídas</TableCell>
                      <TableCell align="center">Óbitos</TableCell>
                      <TableCell align="center">Pacientes-dia</TableCell>
                      <TableCell align="center">Leitos</TableCell>
                      <TableCell align="center">Mortalidade</TableCell>
                      <TableCell align="right">Taxa de ocupação</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {porUnidade.map((u) => (
                      <TableRow key={u.codunidade} hover>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{u.nome}</TableCell>
                        <TableCell align="center">{fmt(u.internacoes)}</TableCell>
                        <TableCell align="center">{fmt(u.saidas)}</TableCell>
                        <TableCell align="center">{fmt(u.obitos)}</TableCell>
                        <TableCell align="center">{fmt(u.pacientes_dia)}</TableCell>
                        <TableCell align="center">{fmt(u.leitos_ativos)}</TableCell>
                        <TableCell align="center">{fmt(u.taxa_mortalidade)}%</TableCell>
                        <TableCell align="right" sx={{ minWidth: 160 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end' }}>
                            <LinearProgress variant="determinate" value={Math.min(u.taxa_ocupacao_hospitalar, 100)} sx={{ width: 80, height: 8, borderRadius: 4 }} />
                            <Typography variant="body2" fontWeight={600}>{fmt(u.taxa_ocupacao_hospitalar)}%</Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography color="text.secondary" variant="body2">Nenhuma internação no período.</Typography>
            )}
          </CardContent>
        </Card>
      )}

      <Chip size="small" variant="outlined" sx={{ mt: 1 }}
        label={`Indicadores de internação no período ${data?.periodo?.inicio ?? ''} a ${data?.periodo?.fim ?? ''}${unidadeSelecionada ? ` • ${unidadeSelecionada.nome}` : ''} • Atualização automática a cada 1 min`} />
    </Box>
  );
};

export default SaudeEstatisticasInternacao;
